const freeSampleState = {
  cart: null,
  busy: false,
  elements: new Set(),
  cartPromise: null,
};

function getConfig() {
  const element = freeSampleState.elements.values().next().value;

  if (!element) {
    return null;
  }

  return {
    productId: Number(element.dataset.productId),
    variantId: Number(element.dataset.variantId),
    thresholdCents: Number(element.dataset.thresholdCents),
  };
}

function getSampleLines(cart, productId) {
  return cart.items.filter((item) => Number(item.product_id) === productId);
}

function isEligible(cart, thresholdCents) {
  return Number(cart.total_price) > thresholdCents;
}

function updateElement(element, cart) {
  const config = {
    productId: Number(element.dataset.productId),
    thresholdCents: Number(element.dataset.thresholdCents),
  };
  const hasSample = getSampleLines(cart, config.productId).some((line) => line.quantity > 0);
  const eligible = isEligible(cart, config.thresholdCents);
  const status = element.querySelector('.bull-free-sample__status');
  const button = element.querySelector('.bull-free-sample__button');
  const optedIn = element.hasAttribute('data-opted-in');

  element.toggleAttribute('data-has-sample', hasSample);
  element.toggleAttribute('data-is-eligible', eligible);

  if (button) {
    const isProductControl = element.dataset.context === 'product';
    button.disabled = freeSampleState.busy || (!isProductControl && !eligible && !hasSample);
    button.textContent = hasSample
      ? element.dataset.removeLabel
      : optedIn
        ? element.dataset.selectedLabel
        : element.dataset.addLabel;
    button.setAttribute('aria-pressed', hasSample || optedIn ? 'true' : 'false');
    button.setAttribute('aria-busy', freeSampleState.busy ? 'true' : 'false');
  }

  if (status) {
    status.textContent = hasSample
      ? element.dataset.addedMessage
      : element.dataset.ineligibleMessage;
  }
}

function updateAll(cart) {
  freeSampleState.cart = cart;
  freeSampleState.elements.forEach((element) => updateElement(element, cart));
}

function prepareSections() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    }),
  );
  return [...new Set(sections)];
}

function announceError() {
  freeSampleState.elements.forEach((element) => {
    const status = element.querySelector('.bull-free-sample__status');
    if (status) status.textContent = element.dataset.errorMessage;
  });
}

async function requestCartChange(endpoint, payload, baseEvent) {
  if (freeSampleState.busy) return null;

  freeSampleState.busy = true;
  if (freeSampleState.cart) updateAll(freeSampleState.cart);

  try {
    const sections = prepareSections();
    const response = await fetch(`${window.Shopify.routes.root}cart/${endpoint}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        ...payload,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || 'Cart update failed');
    }

    let cart = responseJson;
    if (!Array.isArray(cart.items) || typeof cart.total_price === 'undefined') {
      cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((cartResponse) => cartResponse.json());
      cart.sections = responseJson.sections || {};
    }

    updateAll(cart);
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent, cart },
      }),
    );
    return cart;
  } catch (error) {
    console.error('[Bull free sample]', error);
    announceError();
    return null;
  } finally {
    freeSampleState.busy = false;
    if (freeSampleState.cart) {
      updateAll(freeSampleState.cart);
      queueMicrotask(() => normalizeSample(freeSampleState.cart));
    }
  }
}

async function addSample() {
  const config = getConfig();
  const cart = freeSampleState.cart;
  if (!config || !cart || !isEligible(cart, config.thresholdCents)) return;
  if (getSampleLines(cart, config.productId).length > 0) return;

  await requestCartChange(
    'add',
    {
      items: [
        {
          id: config.variantId,
          quantity: 1,
          properties: { _bull_free_sample: 'true' },
        },
      ],
    },
    'free-sample:add',
  );
}

async function removeSample() {
  const config = getConfig();
  const cart = freeSampleState.cart;
  if (!config || !cart) return;

  const updates = {};
  freeSampleState.elements.forEach((element) => {
    element.removeAttribute('data-opted-in');
  });

  getSampleLines(cart, config.productId).forEach((line) => {
    updates[line.key] = 0;
  });

  if (Object.keys(updates).length > 0) {
    await requestCartChange('update', { updates }, 'free-sample:remove');
  }
}

async function normalizeSample(cart) {
  const config = getConfig();
  if (!config || freeSampleState.busy) return;

  const sampleLines = getSampleLines(cart, config.productId);
  const sampleQuantity = sampleLines.reduce((total, line) => total + line.quantity, 0);
  const eligible = isEligible(cart, config.thresholdCents);
  if (sampleQuantity === 0 || (eligible && sampleQuantity === 1 && sampleLines.length === 1)) return;

  const updates = {};
  if (!eligible) {
    sampleLines.forEach((line) => {
      updates[line.key] = 0;
    });
  } else {
    sampleLines.forEach((line, index) => {
      updates[line.key] = index === 0 ? 1 : 0;
    });
  }

  await requestCartChange('update', { updates }, 'free-sample:normalize');
}

async function loadCart() {
  if (!freeSampleState.cartPromise) {
    freeSampleState.cartPromise = fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .finally(() => {
        freeSampleState.cartPromise = null;
      });
  }

  const cart = await freeSampleState.cartPromise;
  updateAll(cart);
  await normalizeSample(cart);
}

class BullFreeSample extends HTMLElement {
  connectedCallback() {
    freeSampleState.elements.add(this);
    this.addEventListener('click', this.onClick);

    if (freeSampleState.cart) {
      updateElement(this, freeSampleState.cart);
    } else {
      loadCart();
    }
  }

  disconnectedCallback() {
    freeSampleState.elements.delete(this);
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const button = event.target.closest('.bull-free-sample__button');
    if (!button) return;

    const config = getConfig();
    const hasSample = config && freeSampleState.cart
      ? getSampleLines(freeSampleState.cart, config.productId).length > 0
      : false;
    const eligible = config && freeSampleState.cart
      ? isEligible(freeSampleState.cart, config.thresholdCents)
      : false;

    if (hasSample) {
      removeSample();
    } else if (this.dataset.context === 'product' && !eligible) {
      this.toggleAttribute('data-opted-in');
      if (freeSampleState.cart) updateElement(this, freeSampleState.cart);
    } else {
      addSample();
    }
  };
}

if (!customElements.get('bull-free-sample')) {
  customElements.define('bull-free-sample', BullFreeSample);
}

document.addEventListener('cart:change', (event) => {
  const cart = event.detail?.cart;
  if (!cart) return;
  updateAll(cart);
  normalizeSample(cart);
});

document.addEventListener('cart:refresh', loadCart);

document.addEventListener('variant:add', (event) => {
  const optedIn = [...freeSampleState.elements].some((element) =>
    element.dataset.context === 'product' && element.hasAttribute('data-opted-in'),
  );
  if (!optedIn || !event.detail?.cart) return;

  updateAll(event.detail.cart);
  addSample();
});
